// shared/src/notifications/thresholds.ts
// Typed interpretation of the NOTIFICATION_THRESHOLDS runtime_param.
//
// The fleet-wide ParamsSchema treats NOTIFICATION_THRESHOLDS as an OPAQUE
// number-map (z.record(number)) — see runtime-params.ts. This module is the
// STRICT, api-worker-side owner of its shape: field metadata (which also drives
// the Params-tab labeled editor), per-field defaults, a total read-resolver, and
// a strict save-validation schema.
//
// WHY: notification thresholds are consumed only by api-worker's
// buildNotifications, so keeping their shape out of the fleet strict schema means
// ADDING A NEW KNOB is api-worker-only — no 9-fixture lockstep, no
// deploy-then-migrate fleet sweep. To add a threshold:
//   1. add one entry to NOTIFICATION_THRESHOLD_FIELDS (label/unit/default),
//   2. add its field to NotificationThresholds + NotificationThresholdsJsonSchema,
//   3. read it in the rule that uses it (+ the RuleContext field),
//   4. deploy api-worker (+ Pages for the labeled form). No fleet, no migration —
//      the new field uses its default until the operator sets it on the Params tab.

import { z } from 'zod';

// ─── Typed (camelCase) view consumed by the rule engine ──────────────────────
export interface NotificationThresholds {
  readonly econWarnDays:   number;
  readonly econCritDays:   number;
  readonly costSpikeMult:  number;
  readonly costSpikeMinUsd:number;
  readonly staleWarnHours: number;
  readonly staleCritHours: number;
  // 2026-07-31 — LLM token-cap headroom. See ruleLlmCapHeadroom in rules.ts.
  readonly capHeadroomWarnRatio:  number;
  readonly capHeadroomCritRatio:  number;
  readonly capHeadroomWindowDays: number;
}

// ─── Field metadata — single source of truth for the labeled editor + mapping ─
// jsonKey = the snake_case key in the stored JSON; ctxKey = the camelCase field
// on NotificationThresholds. `int` marks whole-number fields for the editor.
export interface NotificationThresholdField {
  readonly jsonKey: string;
  readonly ctxKey:  keyof NotificationThresholds;
  readonly label:   string;
  readonly unit:    string;
  readonly step:    number;
  readonly default: number;
  readonly int:     boolean;
  readonly help:    string;
}

export const NOTIFICATION_THRESHOLD_FIELDS: ReadonlyArray<NotificationThresholdField> = [
  { jsonKey: 'econ_warn_days',         ctxKey: 'econWarnDays',    label: 'Econ reminder — warn',     unit: 'd', step: 1,    default: 10,   int: true,  help: 'Warn when the last curated econ-calendar event is within N days.' },
  { jsonKey: 'econ_crit_days',         ctxKey: 'econCritDays',    label: 'Econ reminder — critical', unit: 'd', step: 1,    default: 3,    int: true,  help: 'Critical within N days (or already expired/empty). Must be <= warn.' },
  { jsonKey: 'cost_spike_mult',        ctxKey: 'costSpikeMult',   label: 'Cost spike — multiple',    unit: '×', step: 0.1,  default: 2.0,  int: false, help: "Warn when today's LLM spend is >= N× the trailing 7-day average." },
  { jsonKey: 'cost_spike_min_usd',     ctxKey: 'costSpikeMinUsd', label: 'Cost spike — min spend',   unit: '$', step: 0.01, default: 0.10, int: false, help: 'Ignore the relative spike check below this absolute daily spend (noise floor).' },
  { jsonKey: 'stale_brief_warn_hours', ctxKey: 'staleWarnHours',  label: 'Stale brief — warn',       unit: 'h', step: 1,    default: 30,   int: true,  help: 'Warn when no brief has completed in N hours.' },
  { jsonKey: 'stale_brief_crit_hours', ctxKey: 'staleCritHours',  label: 'Stale brief — critical',   unit: 'h', step: 1,    default: 60,   int: true,  help: 'Critical past N hours (chain likely stuck). Must be >= warn.' },
  // 2026-07-31 — the 0.60 warn default is deliberately LOW, and that is a
  // judgement call worth stating: on the day two briefs died to a token
  // truncation, the worst PRE-failure successful call sat at 0.668 of its cap
  // (8021/12000) and the next observation was the cap itself. The drift was a
  // step function, not a ramp, so any warn above ~0.67 would have been silent
  // through the actual incident.
  { jsonKey: 'llm_cap_warn_ratio',     ctxKey: 'capHeadroomWarnRatio',  label: 'LLM cap headroom — warn',     unit: '×', step: 0.05, default: 0.60, int: false, help: 'Warn when a step’s largest successful response reaches this fraction of its MAX_TOKENS. Low by design: the 2026-07-31 truncation jumped from 0.67 straight to the cap.' },
  { jsonKey: 'llm_cap_crit_ratio',     ctxKey: 'capHeadroomCritRatio',  label: 'LLM cap headroom — critical', unit: '×', step: 0.05, default: 0.80, int: false, help: 'Critical at this fraction of MAX_TOKENS. Must be >= warn.' },
  { jsonKey: 'llm_cap_window_days',    ctxKey: 'capHeadroomWindowDays', label: 'LLM cap headroom — window',   unit: 'd', step: 1,    default: 7,    int: true,  help: 'Look-back window for the per-step max successful completion tokens.' },
];

/** jsonKey → default; the seed value migration 0073 writes + the read fallback. */
export const NOTIFICATION_THRESHOLD_DEFAULTS: Record<string, number> = Object.fromEntries(
  NOTIFICATION_THRESHOLD_FIELDS.map((f) => [f.jsonKey, f.default]),
);

/**
 * Read the opaque number-map into the typed view. TOTAL — a missing or
 * non-finite field falls back to its documented default (so a knob added in a
 * later api-worker deploy works before the operator seeds it). Unknown keys are
 * ignored on read; the strict save schema below rejects them on the next edit.
 */
export function resolveNotificationThresholds(
  raw: Record<string, number> | undefined,
): NotificationThresholds {
  const out: Record<string, number> = {};
  for (const f of NOTIFICATION_THRESHOLD_FIELDS) {
    const v = raw?.[f.jsonKey];
    out[f.ctxKey] = typeof v === 'number' && Number.isFinite(v) ? v : f.default;
  }
  return out as unknown as NotificationThresholds;
}

// ─── Strict save-validation (api-worker admin PATCH) ─────────────────────────
// Rejects a bad Params-tab edit BEFORE it is stored: wrong types, non-positive
// values, unknown keys, and the warn/crit ordering inversions. This is the
// "strict in api-worker" half of the loose-at-fleet design.
export const NotificationThresholdsJsonSchema = z
  .object({
    econ_warn_days:         z.number().int().positive(),
    econ_crit_days:         z.number().int().positive(),
    cost_spike_mult:        z.number().min(1),
    cost_spike_min_usd:     z.number().positive(),
    stale_brief_warn_hours: z.number().int().positive(),
    stale_brief_crit_hours: z.number().int().positive(),
    llm_cap_warn_ratio:     z.number().positive().max(1),
    llm_cap_crit_ratio:     z.number().positive().max(1),
    llm_cap_window_days:    z.number().int().positive(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.econ_warn_days < v.econ_crit_days) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['econ_warn_days'], message: 'econ_warn_days must be >= econ_crit_days' });
    }
    if (v.stale_brief_crit_hours < v.stale_brief_warn_hours) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['stale_brief_crit_hours'], message: 'stale_brief_crit_hours must be >= stale_brief_warn_hours' });
    }
    if (v.llm_cap_crit_ratio < v.llm_cap_warn_ratio) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['llm_cap_crit_ratio'], message: 'llm_cap_crit_ratio must be >= llm_cap_warn_ratio' });
    }
  });
export type NotificationThresholdsJson = z.infer<typeof NotificationThresholdsJsonSchema>;
